import { v4 } from 'uuid';
import React, { useContext, useMemo, useEffect, useRef, useCallback } from 'react';
import { jsx } from 'react/jsx-runtime';
import PropTypes from 'prop-types';

/* eslint-disable class-methods-use-this */
class Tracking {
  constructor(opts = {}) {
    this.options = {
      dataLayer: typeof window !== 'undefined' ? window.dataLayer || null : null,
      disabled: false,
      paused: false,
      variables: null,
      withoutIdleCallback: false,
      ...opts
    };
    const {
      disabled = false,
      paused = false,
      variables = null
    } = this.options;
    this.disabled = disabled;
    this.paused = paused;
    this.variables = null;
    this.pending = [];
    if (variables !== null) {
      this.setVariables(variables);
    }
  }
  setVariables(variables) {
    this.variables = variables;
    if (variables !== null) {
      this.pushNow(variables);
    }
  }
  getVariables() {
    return this.variables;
  }
  setDisabled(disabled) {
    this.disabled = disabled;
    if (disabled) {
      this.pending = [];
    }
  }
  setPaused(paused) {
    this.paused = paused;
    if (!paused && this.pending.length > 0) {
      this.pushNow(...this.pending);
      this.pending = [];
    }
  }
  pushNow(...args) {
    const {
      dataLayer = null,
      withoutIdleCallback = false
    } = this.options;
    if (dataLayer === null || this.disabled) {
      return;
    }
    if (!withoutIdleCallback && 'requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        dataLayer.push(...args);
      });
    } else if (!withoutIdleCallback) {
      setTimeout(() => {
        dataLayer.push(...args);
      }, 0);
    } else {
      dataLayer.push(...args);
    }
  }
  push(...args) {
    if (this.paused && !this.disabled) {
      this.pending.push(...args);
      return;
    }
    this.pushNow(...args);
  }
  pushEvent(eventName, data) {
    this.push({
      event: eventName,
      eventId: v4(),
      ...data
    });
  }
  pushEventNow(eventName, data) {
    this.pushNow({
      event: eventName,
      eventId: v4(),
      ...data
    });
  }
  trackEvent(category, action, label = null, value = null) {
    this.pushEvent('eventInteraction', {
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      eventValue: value
    });
  }
  trackEventNow(category, action, label = null, value = null) {
    this.pushEventNow('eventInteraction', {
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      eventValue: value
    });
  }
  trackSocial(network, action, target = null) {
    this.pushEvent('socialInteraction', {
      socialNetwork: network,
      socialAction: action,
      socialTarget: target || this.getSocialTarget()
    });
  }
  trackVideo(action, {
    platform = null,
    id = null,
    url,
    title = null,
    duration = null,
    currentTime = null,
    thumbnail = null,
    ...data
  } = {}) {
    this.pushEvent('eventInteraction', {
      eventCategory: 'Video',
      eventAction: action,
      eventLabel: `${platform !== null ? `${platform}: ` : ''}${title || document.title}${id !== null ? ` (${id})` : ''}`,
      videoPlatform: platform,
      videoId: id,
      videoUrl: url,
      videoTitle: title,
      videoDuration: duration,
      videoCurrentTime: currentTime !== null ? Math.round(currentTime) : null,
      videoProgress: currentTime !== null && duration !== null && duration > 0 ? Math.round(currentTime / duration * 100) : null,
      videoThumbnail: thumbnail,
      ...data
    });
  }
  getSocialTarget() {
    return typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : null;
  }
}

const TrackingContext = /*#__PURE__*/React.createContext();

const getDisplayName = WrappedComponent => WrappedComponent.displayName || WrappedComponent.name || 'Component';
const withTracking = WrappedComponent => {
  const WithTrackingComponent = props => /*#__PURE__*/jsx(TrackingContext.Consumer, {
    children: tracking => /*#__PURE__*/jsx(WrappedComponent, {
      tracking: tracking,
      ...props
    })
  });
  WithTrackingComponent.displayName = `WithTracking(${getDisplayName(WrappedComponent)})`;
  return WithTrackingComponent;
};

const useTracking = () => useContext(TrackingContext);

const propTypes = {
  tracking: PropTypes.instanceOf(Tracking),
  disabled: PropTypes.bool,
  paused: PropTypes.bool,
  children: PropTypes.node
};
const defaultProps = {
  tracking: null,
  disabled: false,
  paused: false,
  children: null
};
function TrackingContainer({
  children,
  tracking,
  disabled,
  paused
}) {
  const finalTracking = useMemo(() => tracking || new Tracking({
    disabled,
    paused
  }), [tracking]);
  useEffect(() => {
    if (tracking === null) {
      finalTracking.setDisabled(disabled);
      finalTracking.setPaused(paused);
    }
  }, [tracking, disabled, paused]);
  return /*#__PURE__*/jsx(TrackingContext.Provider, {
    value: finalTracking,
    children: children
  });
}
TrackingContainer.propTypes = propTypes;
TrackingContainer.defaultProps = defaultProps;

function useVideoTracking(player, params) {
  if (player === null) {
    return;
  }
  const {
    provider,
    id,
    url,
    title = null,
    thumbnail = null,
    disabled = false,
    progressSteps = [0.1, 0.25, 0.5, 0.75, 0.9, 1.0],
    onProgress = null
  } = params || {};
  const tracking = useTracking();
  const progressTrackedRef = useRef({});
  const {
    playing = false,
    paused = false,
    ended = false,
    currentTime = false,
    duration = null
  } = player;
  const getVideoMetadata = useCallback(metadata => {
    let metadataTitle = null;
    try {
      metadataTitle = title !== null ? decodeURIComponent(title).replace(/[+]+/gi, ' ') : null;
    } catch (e) {
      console.log('error decoding title', e); // eslint-disable-line
    }
    return {
      platform: provider,
      id,
      url,
      title: metadataTitle,
      duration: duration || 0,
      thumbnail,
      ...metadata
    };
  }, [provider, id, title, url, duration, thumbnail]);
  useEffect(() => {
    if (playing && !disabled) {
      tracking.trackVideo('play', getVideoMetadata({
        currentTime
      }));
    }
  }, [playing, disabled]);
  useEffect(() => {
    if (paused && !disabled) {
      tracking.trackVideo('pause', getVideoMetadata({
        currentTime
      }));
    }
  }, [paused, disabled]);
  useEffect(() => {
    if (ended && !disabled) {
      tracking.trackVideo('end', getVideoMetadata({
        currentTime
      }));
    }
  }, [ended, disabled]);
  useEffect(() => {
    if (currentTime === null || currentTime <= 0 || duration === null || duration <= 0 || progressSteps === null || progressSteps.length === 0 || disabled) {
      return;
    }
    const progress = currentTime / duration;
    const stepsToTrack = progressSteps.filter(step => progress > step && (typeof progressTrackedRef.current[id] === 'undefined' || typeof progressTrackedRef.current[id][step] === 'undefined'));
    stepsToTrack.forEach(step => {
      // console.log(
      // Math.round(step * 100, 10),
      //     getVideoMetadata({
      //         currentTime,
      //     }),
      // );
      tracking.trackVideo('progress', getVideoMetadata({
        currentTime,
        videoProgress: Math.round(step * 100, 10)
      }));
      if (onProgress !== null) {
        onProgress(step);
      }
    });
    if (stepsToTrack !== null && stepsToTrack.length > 0) {
      progressTrackedRef.current = {
        ...progressTrackedRef.current,
        [id]: {
          ...progressTrackedRef.current[id],
          ...stepsToTrack.reduce((stepsMap, step) => ({
            ...stepsMap,
            [step]: true
          }), {})
        }
      };
    }
  }, [currentTime, progressTrackedRef.current, id, onProgress, disabled]);
}

export { Tracking, TrackingContainer, TrackingContext, Tracking as default, useTracking, useVideoTracking, withTracking };
